import React, { useContext, useEffect, useState } from "react";
import { StyleSheet, Text, Pressable, View } from "react-native";
import Clipboard from "@react-native-clipboard/clipboard";

import global from "../../../../styles/globalStyles";
import { SessionContext } from "../../../../contexts";
import { getClinicSdkKeys } from "../../../../services/api";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import { buildLeadCaptureShareUrl } from "../../../../utils/leadCaptureLinks";

import { PrimaryButton } from "../../../common/native/index";

import { Close } from "../../../../assets/index";

interface ShareEngageLinkModalProps {
  onClose: () => void;
}

const ShareEngageLinkModal: React.FC<ShareEngageLinkModalProps> = ({
  onClose,
}) => {
  const [copyText, setCopyText] = useState<string>("Generating share link...");

  const session = useContext(SessionContext)?.session;
  const clinicId = session?.clinicId;
  const accessToken = session?.accessToken;
  const isCopyReady = copyText.startsWith("http");

  useEffect(() => {
    const getFormData = async () => {
      if (session) {
        try {
          let clinicSdkKey = session.clinicSdkKeys?.[0];

          if (!clinicSdkKey && accessToken && clinicId) {
            const sdkKeyResponse = await getClinicSdkKeys(
              accessToken,
              clinicId,
            );
            clinicSdkKey = sdkKeyResponse.results?.[0];
          }

          if (!clinicSdkKey) {
            throw new Error("No clinic SDK key available");
          }

          setCopyText(buildLeadCaptureShareUrl({ clinicSdkKey }));
        } catch (error: any) {
          console.error("Error fetching data:", error);
          setCopyText("Lead capture link unavailable");
        }
      }
    };

    getFormData();
  }, [accessToken, clinicId, session]);

  const [isCopied, setIsCopied] = useState<boolean>(false);

  const handleModalClose = () => {
    onClose();
  };

  return (
    <View style={styles.modalView}>
      <View style={styles.titleContainer}>
        <Text style={styles.title}>Share photo capture link</Text>
        <Pressable onPress={handleModalClose}>
          <Close />
        </Pressable>
      </View>
      <Text style={styles.description}>
        Share this lead capture link on Instagram, TikTok, Facebook, or in
        direct messages.
      </Text>
      <View style={styles.copyContainer}>
        <Text style={[global.text]} numberOfLines={1} ellipsizeMode="tail">
          {copyText}
        </Text>
        <PrimaryButton
          text={isCopied ? "Copied" : "Copy"}
          roundBorderRadius
          disabled={!isCopyReady}
          onPress={() => {
            if (!isCopyReady) return;
            Clipboard.setString(copyText);
            setIsCopied(true);
          }}
        />
      </View>
    </View>
  );
};
const styles = StyleSheet.create({
  modalView: {
    width: "100%",
    maxWidth: 465,
    minHeight: 220,
    padding: 24,
    gap: ADMIN_TOKENS.spacing.lg,
    borderRadius: 10,
    backgroundColor: "#fff",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    alignItems: "center",
  },
  titleContainer: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontFamily: "Urbanist600",
    fontSize: 20,
    lineHeight: 20 * 1.4,
    letterSpacing: 0.2,
    fontStyle: "normal",
  },
  description: {
    width: "100%",
    fontFamily: ADMIN_TOKENS.font.medium,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
    color: ADMIN_TOKENS.color.textSecondary,
  },
  iconsContainer: {
    flexDirection: "row",
    marginRight: "auto",
    gap: 10,
  },
  iconPressable: {
    width: 60,
    height: 60,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: "#DDDEE0",
    backgroundColor: "#F8F8F8",
    position: "relative",
  },
  copyContainer: {
    width: "100%",
    margin: "auto",
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 10,
    borderColor: "#C4C6CD",
    borderWidth: 1,
    borderRadius: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

export default ShareEngageLinkModal;
